import type { LocationQueryValue } from '#vue-router'

import type { Config, NumberConfig, PriceList, ReactiveSource } from '../@types/editor'
import type { QuickPickSettings } from '../stores/pickLottery'

import {
  EDITOR_QUERY_QUICK_PICK,
  EDITOR_QUERY_QUICK_PICK_BET_TYPE,
  EDITOR_QUERY_QUICK_PICK_NUMBERS,
  EDITOR_QUERY_QUICK_PICK_WAGER,
  EDITOR_QUICK_PICK_QUERY_PARAMS,
} from '../constants/editor'
import { getPreparedWagers } from '../utils/editorWager'

type QueryValue = LocationQueryValue | LocationQueryValue[] | undefined

type QuickPickQueryStore = {
  quickPickPlays: (config: Config, quantity: number) => string[]
  quickPickPlaysWithSettings?: (config: Config, settings: QuickPickSettings) => string[]
  resetPlays: () => void
}

const DEFAULT_QUANTITY = 1

const QUICK_PICK_ALL = 'true'

const normalize = (value: string) => value.toLowerCase().replace(/[^0-9a-z.]/gi, '')

const toSingleValue = (value: QueryValue): Maybe<string> => {
  const singleValue = Array.isArray(value) ? value[0] : value
  return typeof singleValue === 'string' ? singleValue : null
}

const isInteger = (value: Maybe<string>): value is string =>
  !!value?.length && Number.isInteger(Number(value))

export const toQuickPickQuantity = (value: QueryValue, config: Config): Maybe<number> => {
  const singleValue = toSingleValue(value)
  if (singleValue === QUICK_PICK_ALL) return config.quickPick?.plays ?? DEFAULT_QUANTITY
  if (!isInteger(singleValue) || Number(singleValue) <= 0) return null
  return Number(singleValue)
}

export const useEditorQuickPickQuery = (
  store: QuickPickQueryStore,
  configRef: ReactiveSource<Maybe<Config>>,
  priceListRef?: ReactiveSource<Maybe<PriceList> | undefined>,
) => {
  const route = useRoute()
  const { removeQueryParams } = useRouterUtils()
  const { scrollToCenteredPlay } = useEditorScroll()
  const { hold } = useEditorSettling()

  // without a quick pick param the plays render right away, on the server as well as on the client
  const quickPickReady = ref(!route.query[EDITOR_QUERY_QUICK_PICK])

  const toBetType = (config: Config): Maybe<string> => {
    const value = toSingleValue(route.query[EDITOR_QUERY_QUICK_PICK_BET_TYPE])
    if (!value) return null

    const options = config.addOns.find(({ perPlay }) => perPlay)?.options ?? []
    return options.find((option) => normalize(option) === normalize(value)) ?? null
  }

  const toFixedNumber = (value: QueryValue, { selectableNumbers }: NumberConfig): Maybe<number> => {
    const singleValue = toSingleValue(value)
    if (!isInteger(singleValue)) return null

    const fixedNumber = Number(singleValue)
    return selectableNumbers.includes(fixedNumber) ? fixedNumber : null
  }

  const toNumbers = (config: Config): Maybe<Maybe<number>[]> => {
    const value = route.query[EDITOR_QUERY_QUICK_PICK_NUMBERS]
    if (value === undefined) return null

    const values = Array.isArray(value) ? value : [value]
    return config.numbers.map((numberConfig, index) => toFixedNumber(values[index], numberConfig))
  }

  const toStake = (config: Config): Maybe<string> => {
    const value = toSingleValue(route.query[EDITOR_QUERY_QUICK_PICK_WAGER])
    const priceList = toValue(priceListRef)
    if (!isInteger(value) || !priceList) return null

    const wager = Number(value)
    return getPreparedWagers(config, priceList).find((entry) => entry.value === wager)?.id ?? null
  }

  const fillPlays = (config: Config, quantity: number): string[] => {
    const { quickPickPlaysWithSettings } = store
    if (!quickPickPlaysWithSettings) return store.quickPickPlays(config, quantity)

    return quickPickPlaysWithSettings(config, {
      betType: toBetType(config),
      numbers: toNumbers(config),
      quantity,
      stake: toStake(config),
    })
  }

  const stripQueryParams = async () => {
    try {
      await removeQueryParams(...EDITOR_QUICK_PICK_QUERY_PARAMS)
    } catch {
      // a failed navigation must not keep the editor hidden
    }
  }

  const fillFromQuery = (): Maybe<string[]> => {
    try {
      const config = toValue(configRef)
      const value = route.query[EDITOR_QUERY_QUICK_PICK]
      if (!config || !value?.length) return null

      const quantity = toQuickPickQuantity(value, config)
      if (!quantity) return []

      store.resetPlays()
      return fillPlays(config, quantity)
    } finally {
      quickPickReady.value = true
    }
  }

  const applyQuickPickFromQuery = async () => {
    const release = hold()

    try {
      const playIds = fillFromQuery()
      if (!playIds) return

      await stripQueryParams()
      await scrollToCenteredPlay(playIds.at(-1) ?? null)
    } finally {
      release()
    }
  }

  return { applyQuickPickFromQuery, quickPickReady }
}
